import { NetworkStatus, useQuery } from '@apollo/client';
import {
  CheckCircleIcon,
  ClipboardCopyIcon,
  ExclamationIcon,
  ExternalLinkIcon,
  LocationMarkerIcon,
  RefreshIcon,
  TruckIcon,
} from '@heroicons/react/outline';
import classNames from 'classnames';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import { Button } from 'components/Button';
import { MERCHANT_TRACKING_DETAIL } from 'gql/queries';
import {
  MerchantTrackingDetailData,
  MerchantTrackingDetailVariables,
  MerchantTrackingOwnerType,
  MerchantTrackingProgressStage,
} from 'types/tracking';
import { SizeEnum } from 'utils';

const TRACKING_POLL_INTERVAL_MS = 30_000;
const COPY_FEEDBACK_DURATION_MS = 3_000;

const PROGRESS_STEPS: Array<{
  stage: Exclude<MerchantTrackingProgressStage, 'EXCEPTION' | 'UNKNOWN'>;
  label: string;
}> = [
  { stage: 'LABEL_CREATED', label: 'Label created' },
  { stage: 'IN_TRANSIT', label: 'In transit' },
  { stage: 'OUT_FOR_DELIVERY', label: 'Out for delivery' },
  { stage: 'DELIVERED', label: 'Delivered' },
];

const formatTimestamp = (value?: string | null) => {
  if (!value) return 'Not available';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not available';

  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
};

const formatEta = (value?: string | null) => {
  if (!value) return 'Not available';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not available';

  const normalizedValue = value.trim();
  const isDateOnly =
    /^\d{4}-\d{2}-\d{2}$/.test(normalizedValue) ||
    /^\d{4}-\d{2}-\d{2}T00:00:00(?:\.000)?Z$/.test(normalizedValue);

  if (!isDateOnly) return formatTimestamp(value);

  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(date);
};

const safeCarrierUrl = (value?: string | null) => {
  if (!value) return null;

  try {
    const url = new URL(value);
    return url.protocol === 'https:' || url.protocol === 'http:'
      ? url.toString()
      : null;
  } catch (_error) {
    return null;
  }
};

const safeOwnerPath = (
  ownerType: MerchantTrackingOwnerType,
  ownerId: string,
  value?: string | null,
) => {
  const expectedPrefix = ownerType === 'RETURN' ? '/returns/' : '/orders/';
  return value?.startsWith(expectedPrefix)
    ? value
    : `${expectedPrefix}${encodeURIComponent(ownerId)}`;
};

const DetailState = ({
  title,
  description,
  action,
}: {
  title: string;
  description: string;
  action?: React.ReactNode;
}) => (
  <div className="max-w-3xl mx-auto px-4 sm:px-6 py-20">
    <div className="rounded-lg border border-gray-200 bg-white px-6 py-12 text-center shadow-sm">
      <TruckIcon
        className="mx-auto h-10 w-10 text-gray-400"
        aria-hidden="true"
      />
      <h1 className="mt-4 text-xl font-semibold text-gray-900">{title}</h1>
      <p className="mx-auto mt-2 max-w-xl text-sm text-gray-600">
        {description}
      </p>
      {action && <div className="mt-6">{action}</div>}
    </div>
  </div>
);

const ShipmentProgress = ({
  stage,
}: {
  stage: MerchantTrackingProgressStage;
}) => {
  const currentIndex = PROGRESS_STEPS.findIndex((step) => step.stage === stage);

  return (
    <ol
      aria-label="Shipment progress"
      className="grid grid-cols-1 gap-3 sm:grid-cols-4 sm:gap-0"
    >
      {PROGRESS_STEPS.map((step, index) => {
        const reached = currentIndex >= index;
        const current = currentIndex === index;

        return (
          <li
            key={step.stage}
            className="relative flex items-center sm:block"
            aria-current={current ? 'step' : undefined}
          >
            {index > 0 && (
              <div
                className={classNames(
                  'absolute right-1/2 top-4 hidden h-0.5 w-full sm:block',
                  reached ? 'bg-blue-600' : 'bg-gray-200',
                )}
                aria-hidden="true"
              />
            )}
            <span
              className={classNames(
                'relative z-10 flex h-8 w-8 flex-none items-center justify-center rounded-full border-2 text-xs font-semibold',
                reached
                  ? 'border-blue-600 bg-blue-600 text-white'
                  : 'border-gray-300 bg-white text-gray-500',
              )}
              aria-hidden="true"
            >
              {reached ? <CheckCircleIcon className="h-5 w-5" /> : index + 1}
            </span>
            <span
              className={classNames(
                'ml-3 text-sm font-medium sm:mt-2 sm:block sm:ml-0',
                current
                  ? 'text-blue-700'
                  : reached
                  ? 'text-gray-900'
                  : 'text-gray-500',
              )}
            >
              {step.label}
            </span>
          </li>
        );
      })}
    </ol>
  );
};

const Warning = ({ title, message }: { title: string; message?: string }) => (
  <div
    className="rounded-md border border-amber-200 bg-amber-50 p-4"
    role="alert"
  >
    <div className="flex">
      <ExclamationIcon
        className="mt-0.5 h-5 w-5 flex-none text-amber-500"
        aria-hidden="true"
      />
      <div className="ml-3">
        <p className="text-sm font-medium text-amber-900">{title}</p>
        {message && <p className="mt-1 text-sm text-amber-800">{message}</p>}
      </div>
    </div>
  </div>
);

export const TrackingDetail = () => {
  const { trackingNumber: routeTrackingNumber } = useParams();
  const [searchParams] = useSearchParams();
  const [copyFeedback, setCopyFeedback] = useState('');
  const [refreshError, setRefreshError] = useState('');
  const [manualRefreshing, setManualRefreshing] = useState(false);
  const copyFeedbackTimer = useRef<number | null>(null);

  const trackingNumber = (routeTrackingNumber || '').trim();
  const requestedOwnerType = searchParams.get('ownerType');
  const ownerId = (searchParams.get('ownerId') || '').trim();
  const ownerType: MerchantTrackingOwnerType | null =
    requestedOwnerType === 'ORDER' || requestedOwnerType === 'RETURN'
      ? requestedOwnerType
      : null;
  const hasValidContext = Boolean(trackingNumber && ownerType && ownerId);

  const { data, loading, error, refetch, networkStatus } = useQuery<
    MerchantTrackingDetailData,
    MerchantTrackingDetailVariables
  >(MERCHANT_TRACKING_DETAIL, {
    variables: {
      trackingNumber,
      ownerType: ownerType || 'ORDER',
      ownerId,
    },
    skip: !hasValidContext,
    pollInterval: TRACKING_POLL_INTERVAL_MS,
    notifyOnNetworkStatusChange: true,
  });

  const detail = data?.merchantTrackingDetail || null;
  const carrierUrl = safeCarrierUrl(detail?.carrierTrackingUrl);
  const isRefreshing =
    manualRefreshing || networkStatus === NetworkStatus.refetch;
  const previousNetworkStatus = useRef(networkStatus);

  useEffect(() => {
    const queryRecovered =
      previousNetworkStatus.current !== NetworkStatus.ready &&
      networkStatus === NetworkStatus.ready &&
      !error;

    if (queryRecovered) setRefreshError('');
    previousNetworkStatus.current = networkStatus;
  }, [error, networkStatus]);

  useEffect(
    () => () => {
      if (copyFeedbackTimer.current !== null) {
        window.clearTimeout(copyFeedbackTimer.current);
      }
    },
    [],
  );

  const visibleWarnings = useMemo(() => {
    if (!detail) return [];

    const entries: Array<{ title: string; message?: string }> = [];
    if (detail.progressStage === 'EXCEPTION') {
      entries.push({
        title: 'Shipment exception',
        message: 'The carrier reported an issue that may delay this shipment.',
      });
    }
    if (detail.progressStage === 'UNKNOWN') {
      entries.push({
        title: 'Tracking status is unknown',
        message: 'SHIPAID has not received a recognizable carrier status yet.',
      });
    }
    if (detail.stale) {
      entries.push({
        title: 'Tracking data may be stale',
        message:
          'No recent tracking update has been received from the carrier.',
      });
    }
    if (detail.unmapped) {
      entries.push({
        title: 'Tracking status needs review',
        message:
          'The latest carrier status could not be mapped to a SHIPAID status.',
      });
    }
    if (error) {
      entries.push({
        title: 'Latest refresh failed',
        message:
          'The tracking details below are the last data SHIPAID received.',
      });
    }
    detail.warnings.forEach((warning) =>
      entries.push({ title: warning.message }),
    );

    return entries.filter(
      (entry, index) =>
        entries.findIndex((candidate) => candidate.title === entry.title) ===
        index,
    );
  }, [detail, error]);

  const showCopyFeedback = (message: string) => {
    if (copyFeedbackTimer.current !== null) {
      window.clearTimeout(copyFeedbackTimer.current);
    }

    setCopyFeedback(message);
    copyFeedbackTimer.current = window.setTimeout(() => {
      setCopyFeedback('');
      copyFeedbackTimer.current = null;
    }, COPY_FEEDBACK_DURATION_MS);
  };

  const handleCopy = async () => {
    const clipboard = navigator.clipboard;
    if (typeof clipboard?.writeText !== 'function') {
      showCopyFeedback('Unable to copy the tracking number.');
      return;
    }

    try {
      await clipboard.writeText(trackingNumber);
      showCopyFeedback('Tracking number copied.');
    } catch (_error) {
      showCopyFeedback('Unable to copy the tracking number.');
    }
  };

  const handleRefresh = async () => {
    setManualRefreshing(true);
    setRefreshError('');
    try {
      await refetch();
    } catch (_error) {
      setRefreshError('SHIPAID could not refresh tracking data. Try again.');
    } finally {
      setManualRefreshing(false);
    }
  };

  if (!hasValidContext) {
    return (
      <DetailState
        title="Tracking link is incomplete"
        description="Open tracking from its linked order, return, or issue so SHIPAID can identify the correct shipment."
      />
    );
  }

  if (loading && !detail) {
    return (
      <div
        className="flex min-h-[24rem] items-center justify-center text-sm font-medium text-gray-600"
        role="status"
        aria-live="polite"
      >
        <RefreshIcon className="mr-2 h-5 w-5 animate-spin" aria-hidden="true" />
        Loading tracking details
      </div>
    );
  }

  const accessError = error?.graphQLErrors?.some((graphQLError) =>
    ['FORBIDDEN', 'UNAUTHENTICATED', 'NOT_FOUND'].includes(
      String(graphQLError.extensions?.code || '').toUpperCase(),
    ),
  );

  if (!detail && (accessError || !error)) {
    return (
      <DetailState
        title="Tracking unavailable"
        description="This tracking record was not found or you do not have access to it. Confirm that you opened it from the correct store."
      />
    );
  }

  if (!detail) {
    return (
      <DetailState
        title="We couldn't load tracking"
        description="SHIPAID could not reach the tracking service. Check your connection and try again."
        action={
          <Button size={SizeEnum.sm} onClick={handleRefresh}>
            Try again
          </Button>
        }
      />
    );
  }

  const ownerTitle = detail.ownerType === 'RETURN' ? 'Return' : 'Order';
  const ownerLabel = [ownerTitle, detail.ownerNumber].filter(Boolean).join(' ');
  const ownerPath = safeOwnerPath(
    detail.ownerType,
    detail.ownerId,
    detail.ownerPath,
  );

  return (
    <div className="mx-auto max-w-7xl px-4 pb-12 sm:px-6 md:px-8">
      <div className="mb-5 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <Link
            to={detail.ownerType === 'RETURN' ? '/returns' : '/orders'}
            className="text-sm font-medium text-blue-700 hover:text-blue-800"
          >
            ← Back to {detail.ownerType === 'RETURN' ? 'returns & exchanges' : 'orders'}
          </Link>
          <h1 className="mt-2 break-all text-xl font-semibold text-gray-900 sm:text-2xl">
            Tracking {detail.trackingNumber}
          </h1>
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-600">
            <span className="inline-flex items-center rounded-full bg-blue-50 px-3 py-1 font-medium text-blue-700">
              {detail.statusLabel}
            </span>
            <Link
              to={ownerPath}
              className="font-medium text-blue-700 hover:underline"
            >
              {ownerLabel}
            </Link>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            size={SizeEnum.sm}
            variant="outlined"
            onClick={handleCopy}
            props={{ 'aria-label': 'Copy tracking number' }}
          >
            <ClipboardCopyIcon className="mr-2 h-4 w-4" aria-hidden="true" />
            Copy
          </Button>
          {carrierUrl && (
            <a
              href={carrierUrl}
              target="_blank"
              rel="noreferrer"
              aria-label="Open carrier tracking"
              className="inline-flex items-center rounded-md border border-blue-700 bg-white px-3 py-2 text-sm font-medium leading-4 text-gray-900 shadow-sm hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              <ExternalLinkIcon className="mr-2 h-4 w-4" aria-hidden="true" />
              Carrier page
            </a>
          )}
          <Button
            size={SizeEnum.sm}
            onClick={handleRefresh}
            disabled={isRefreshing}
            props={{
              'aria-label': isRefreshing
                ? 'Refreshing SHIPAID data'
                : 'Refresh SHIPAID data',
            }}
          >
            <RefreshIcon
              className={classNames(
                'mr-2 h-4 w-4',
                isRefreshing && 'animate-spin',
              )}
              aria-hidden="true"
            />
            {isRefreshing ? 'Refreshing' : 'Refresh'}
          </Button>
        </div>
      </div>

      {copyFeedback && (
        <p
          className="mb-4 text-sm text-gray-700"
          role="status"
          aria-live="polite"
        >
          {copyFeedback}
        </p>
      )}
      {refreshError && (
        <p className="mb-4 text-sm font-medium text-red-700" role="alert">
          {refreshError}
        </p>
      )}

      {visibleWarnings.length > 0 && (
        <div className="mb-5 space-y-2">
          {visibleWarnings.map((warning) => (
            <Warning
              key={`${warning.title}-${warning.message || ''}`}
              title={warning.title}
              message={warning.message}
            />
          ))}
        </div>
      )}

      <section className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
        <div className="mb-5 flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold text-gray-900">
              Current status
            </h2>
            <p className="mt-1 text-sm text-gray-600">{detail.statusLabel}</p>
          </div>
          <TruckIcon className="h-7 w-7 text-blue-600" aria-hidden="true" />
        </div>
        <ShipmentProgress stage={detail.progressStage} />
      </section>

      <section className="mt-5 rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
        <h2 className="text-base font-semibold text-gray-900">
          Shipment details
        </h2>
        <dl className="mt-4 grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-4">
          {[
            ['Carrier', detail.carrier || 'Not available'],
            ['Service', detail.service || 'Not available'],
            ['Estimated delivery', formatEta(detail.eta)],
            ['Last SHIPAID update', formatTimestamp(detail.lastUpdatedAt)],
          ].map(([label, value]) => (
            <div key={label}>
              <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
                {label}
              </dt>
              <dd className="mt-1 text-sm font-medium text-gray-900">
                {value}
              </dd>
            </div>
          ))}
        </dl>
      </section>

      <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-3">
        <section className="rounded-lg border border-gray-200 bg-white shadow-sm lg:col-span-2">
          <div className="border-b border-gray-200 px-5 py-4">
            <h2 className="text-base font-semibold text-gray-900">
              Checkpoint timeline
            </h2>
          </div>
          {detail.checkpoints.length === 0 ? (
            <div className="px-5 py-10 text-center">
              <LocationMarkerIcon
                className="mx-auto h-8 w-8 text-gray-400"
                aria-hidden="true"
              />
              <p className="mt-3 text-sm font-medium text-gray-900">
                No carrier scans yet
              </p>
              <p className="mt-1 text-sm text-gray-600">
                Checkpoints will appear after the carrier scans the shipment.
              </p>
            </div>
          ) : (
            <ol className="divide-y divide-gray-100 px-5">
              {detail.checkpoints.map((checkpoint, index) => (
                <li
                  key={`${checkpoint.occurredAt || 'undated'}-${
                    checkpoint.message
                  }-${index}`}
                  className="relative py-5 pl-8"
                >
                  <span
                    className="absolute left-0 top-6 h-3 w-3 rounded-full border-2 border-blue-600 bg-white"
                    aria-hidden="true"
                  />
                  <p className="text-sm font-medium text-gray-900">
                    {checkpoint.message}
                  </p>
                  <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-500">
                    <time dateTime={checkpoint.occurredAt || undefined}>
                      {formatTimestamp(checkpoint.occurredAt)}
                    </time>
                    {checkpoint.location && <span>{checkpoint.location}</span>}
                    {checkpoint.status && (
                      <span>{checkpoint.status.replace(/_/g, ' ')}</span>
                    )}
                  </div>
                </li>
              ))}
            </ol>
          )}
        </section>

        <section className="rounded-lg border border-gray-200 bg-white shadow-sm">
          <div className="border-b border-gray-200 px-5 py-4">
            <h2 className="text-base font-semibold text-gray-900">
              Included items
            </h2>
          </div>
          {detail.items.length === 0 ? (
            <p className="px-5 py-8 text-sm text-gray-600">
              No item details available.
            </p>
          ) : (
            <ul className="divide-y divide-gray-100 px-5">
              {detail.items.map((item) => (
                <li key={item.id} className="flex gap-3 py-4">
                  {item.imageUrl ? (
                    <img
                      src={item.imageUrl}
                      alt=""
                      className="h-12 w-12 flex-none rounded border border-gray-200 object-cover"
                    />
                  ) : (
                    <div
                      className="h-12 w-12 flex-none rounded bg-gray-100"
                      aria-hidden="true"
                    />
                  )}
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900">
                      {item.title}
                    </p>
                    <p className="mt-1 text-xs text-gray-500">
                      Quantity: {item.quantity}
                    </p>
                    {item.sku && (
                      <p className="text-xs text-gray-500">SKU: {item.sku}</p>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
};
